import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { usePet } from '../contexts/PetContext';
import HpQuiz from './HpQuiz';
import HpStatsExtra from './HpStatsExtra';
import HpGossip from './HpGossip';
import HpPalaceWalk from './HpPalaceWalk';
import GuidedHypnosisPlayer from './GuidedHypnosisPlayer';
import { HampusReminderSettings } from './HampusReminder';
import HpWeeklyLetter from './HpWeeklyLetter';
import HpFlashcards from './HpFlashcards';
import HpExamDay from './HpExamDay';
import HampusWardrobe, { HampusAvatar } from './HampusWardrobe';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CALM_KEY = 'hp-calm-before';

function DayDots({ day, total }) {
  return (
    <div className="flex gap-1 flex-wrap" data-testid="hp-daily-dots">
      {Array.from({ length: total }, (_, i) => (
        <span key={i} className={`w-2.5 h-2.5 rounded-full border border-black/40 ${i + 1 < day ? 'bg-[#F5B841]' : i + 1 === day ? 'bg-black' : 'bg-white'}`} />
      ))}
    </div>
  );
}

export default function HpDailyPass() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const [calm, setCalm] = useState(null);
  const [calmOn, setCalmOn] = useState(() => localStorage.getItem(CALM_KEY) !== 'off');
  const [statsKey, setStatsKey] = useState(0);
  const { refreshPet } = usePet();

  const [hampus, setHampus] = useState(null);
  const load = () => axios.get(`${API}/hp-daily/today`, { withCredentials: true }).then((r) => setData(r.data)).catch(() => {});
  const loadHampus = () => axios.get(`${API}/hp-daily/hampus`, { withCredentials: true }).then((r) => {
    setHampus(r.data);
    if (r.data.new_unlock) {
      toast.success(`🎁 Hampus låste upp: ${r.data.new_unlock.emoji} ${r.data.new_unlock.name}!`);
      axios.post(`${API}/hp-daily/hampus/seen`, {}, { withCredentials: true }).catch(() => {});
    }
  }).catch(() => {});
  useEffect(() => { load(); loadHampus(); }, []);

  const run = async (kind, fn) => {
    setBusy(kind);
    try { await fn(); } catch (e) { toast.error(e?.response?.data?.detail || 'Något gick snett 🐹'); }
    setBusy('');
  };
  const join = () => run('join', async () => {
    await axios.post(`${API}/challenges/hp-14/join`, {}, { withCredentials: true });
    toast.success('🐹 Du är med! 14 dagar, en del om dagen');
    await load();
  });
  const generate = () => run('gen', async () => {
    const r = await axios.post(`${API}/hp-daily/today/generate`, {}, { withCredentials: true, timeout: 90000 });
    setData(r.data);
  });
  const start = async (withCalm) => {
    if (withCalm) {
      try {
        const { data: s } = await axios.post(`${API}/hypnos/guided/exam-calm`, {}, { withCredentials: true });
        setCalm(s);
      } catch { /* calm is optional */ }
    }
    generate();
  };
  const toggleCalm = () => {
    localStorage.setItem(CALM_KEY, calmOn ? 'off' : 'on');
    setCalmOn(!calmOn);
  };
  const submit = (answers) => run('submit', async () => {
    const r = await axios.post(`${API}/hp-daily/today/submit`, { answers }, { withCredentials: true });
    setData(r.data);
    setStatsKey((k) => k + 1);
    window.dispatchEvent(new Event('hampus:pass-done'));
    toast.success(`🐹 ${r.data.score}/${r.data.questions.length} rätt · +${r.data.xp} XP${r.data.checked_in ? ' · dagen incheckad ✓' : ''}`);
    refreshPet?.();
    loadHampus();
  });

  if (!data) return <div className="neo-card p-4 bg-[#FFF6D6] animate-pulse h-32" data-testid="hp-daily-skeleton" />;
  const { part, questions } = data;

  return (
    <div className="space-y-3">
      <HpWeeklyLetter refreshKey={statsKey} />
      <section className="neo-card p-4 bg-gradient-to-br from-[#FFF6D6] via-[#FFE8A3] to-[#FFF0E0] space-y-3" data-testid="hp-daily-pass">
        <div className="flex items-start gap-3">
          <HampusAvatar state={hampus} base={48} />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-bold tracking-[0.25em] uppercase opacity-60">Dagens HP-pass · dag {data.day_number}/{data.total_days}</p>
            <h3 className="font-['Fredoka'] font-bold text-xl leading-tight" data-testid="hp-daily-part">{part.emoji} {part.name} · {part.fullname}</h3>
            {data.adaptive && <span className="inline-block mt-1 text-[10px] font-black px-2 py-0.5 rounded-full bg-[#C24672] text-white" data-testid="hp-daily-adaptive">🎯 Anpassad: en av dina svagaste delar</span>}
            <p className="text-xs opacity-70 mt-0.5">{part.description}</p>
            <div className="mt-2"><DayDots day={data.day_number} total={data.total_days} /></div>
          </div>
        </div>
        {part.tips?.length > 0 && !questions.length && (
          <ul className="text-xs space-y-1 bg-white/60 rounded-xl p-2.5" data-testid="hp-daily-tips">
            {part.tips.map((t, i) => <li key={i}>✨ {t}</li>)}
          </ul>
        )}
        {!questions.length && (
          <div className="flex flex-wrap items-center gap-2">
            {!data.joined && (
              <button onClick={join} disabled={!!busy} className="neo-btn px-4 py-2 bg-white font-bold text-sm" data-testid="hp-daily-join">
                {busy === 'join' ? <Loader2 className="w-4 h-4 animate-spin" /> : '🏆 Gå med i 14-dagarsutmaningen'}
              </button>
            )}
            <button onClick={() => start(calmOn)} disabled={!!busy} className="neo-btn px-4 py-2 bg-[#F5B841] font-bold text-sm flex items-center gap-1" data-testid="hp-daily-start">
              {busy === 'gen' ? <><Loader2 className="w-4 h-4 animate-spin" /> Hampus hämtar frågor…</> : `▶ Starta dagens pass${calmOn ? ' (med 2 min lugn)' : ''}`}
            </button>
            {!calmOn && (
              <button onClick={() => start(true)} disabled={!!busy} className="neo-btn px-3 py-2 bg-[#2a1d4f] text-white font-bold text-sm" data-testid="hp-daily-calm-btn">🌙 2 min lugn innan</button>
            )}
            <label className="flex items-center gap-1.5 text-xs font-bold cursor-pointer" data-testid="hp-daily-calm-toggle">
              <input type="checkbox" checked={calmOn} onChange={toggleCalm} className="accent-[#2a1d4f]" /> Lugn före varje pass
            </label>
          </div>
        )}
        <HpQuiz quiz={data} onSubmit={submit} busy={busy === 'submit'} doneText="Bra jobbat! Imorgon väljer Hampus en ny del åt dig 💛" />
      </section>
      <HpGossip />
      <HpFlashcards refreshKey={statsKey} />
      <HpExamDay onDone={() => setStatsKey((k) => k + 1)} />
      <HpPalaceWalk />
      <HpStatsExtra refreshKey={statsKey} />
      <HampusWardrobe state={hampus} onChange={setHampus} />
      <HampusReminderSettings />
      {calm && <GuidedHypnosisPlayer session={calm} voiceOn onClose={() => setCalm(null)} onFinish={() => setCalm(null)} />}
    </div>
  );
}
